import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const ORDER_IMG = `${process.env.PUBLIC_URL || ""}/brand/cat-box.jpg`;

const OrderCTA = () => {
  const { lang } = useLang();
  const fr = content.order.fr;
  const en = content.order.en;
  const isBoth = lang === "both";
  const data = lang === "en" ? en : fr;

  return (
    <section
      id="order"
      data-testid="order-section"
      className="relative overflow-hidden section"
      style={{ background: "var(--bb-yellow)" }}
    >
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 bg-black text-[var(--bb-yellow)] rounded-full px-4 py-2 text-[11px] font-bold tracking-[0.2em] uppercase">
              <Flame size={12} strokeWidth={2.5} />
              <span>{isBoth ? `${fr.eyebrow} · ${en.eyebrow}` : data.eyebrow}</span>
            </span>

            <h2
              data-testid="order-title"
              className="mt-5 h-display text-black text-[40px] sm:text-[56px] md:text-[68px] lg:text-[80px] leading-[0.95]"
            >
              <span className="block">{data.title}</span>
              <span className="block italic">{data.titleAccent}</span>
            </h2>

            <p className="mt-5 text-base md:text-lg text-black/80 max-w-xl">
              {data.sub}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={content.externalLinks.order}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="order-cta-button"
                className="bb-btn bb-btn-black bb-btn-lg bb-btn-mobile-full"
              >
                <Flame size={16} strokeWidth={2.5} />
                <span>{data.cta}</span>
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
              <a
                href={content.externalLinks.order}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold uppercase tracking-wider self-center text-black/75 hover:text-black underline underline-offset-4 decoration-2"
              >
                {lang === "en" ? "View full menu →" : "Voir le menu complet →"}
              </a>
            </div>

            {/* Stats row */}
            <div className="mt-10 grid grid-cols-3 gap-3 md:gap-4 max-w-lg">
              <div data-testid="order-stat-1">
                <div className="font-display text-2xl md:text-4xl text-black leading-none">
                  {data.stat1}
                </div>
                <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-black/70">
                  {data.stat1Label}
                </div>
              </div>
              <div data-testid="order-stat-2" className="border-l-2 border-black/30 pl-3 md:pl-4">
                <div className="font-display text-2xl md:text-4xl text-black leading-none">
                  {data.stat2}
                </div>
                <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-black/70">
                  {data.stat2Label}
                </div>
              </div>
              <div data-testid="order-stat-3" className="border-l-2 border-black/30 pl-3 md:pl-4">
                <div className="font-display text-2xl md:text-4xl text-black leading-none">
                  {data.stat3}
                </div>
                <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-black/70">
                  {data.stat3Label}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="bb-img-zoom h-[280px] sm:h-[360px] lg:h-[460px] border-[3px] border-black rounded-[24px]">
                <img src={ORDER_IMG} alt="Big Boss food" loading="lazy" />
              </div>
              <div className="hidden md:flex absolute -top-5 -right-5 h-24 w-24 lg:h-28 lg:w-28 rounded-full bg-black text-[var(--bb-yellow)] items-center justify-center rotate-[-12deg] border-[3px] border-[var(--bb-yellow)]">
                <div className="text-center">
                  <div className="font-display text-[10px] tracking-[0.22em] uppercase">
                    {lang === "en" ? "From" : "Dès"}
                  </div>
                  <div className="font-display text-3xl leading-none mt-0.5">7.-</div>
                  <div className="font-body text-[10px] uppercase tracking-[0.18em] mt-0.5">
                    CHF
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OrderCTA;
